<template>
  <v-card>
    <v-toolbar dark color="primary">
      <v-btn icon dark @click="closeDialog">
        <v-icon>mdi-close</v-icon>
      </v-btn>
      <v-toolbar-title v-if="!!editing && !cloning">{{ $t('components.CreateCustomModel.editTitle') }}</v-toolbar-title>
      <v-toolbar-title v-else>{{ $t('components.CreateCustomModel.newTitle') }}</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-toolbar-items>
        <v-btn dark text @click="triggerCreate" v-if="!editing || (editing && cloning)">{{ $t('components.CreateCustomModel.create') }}</v-btn>
        <v-btn dark text @click.stop="saveDialog = true" v-else>{{ $t('components.CreateCustomModel.save') }}</v-btn>
        <v-dialog v-model="saveDialog" max-width="400">
          <v-card>
            <v-card-title class="headline">{{ $t('components.CreateCustomModel.warning') }}</v-card-title>
            <v-card-text>
              {{ $t('components.CreateCustomModel.saveWarningMessage') }}
              <br/>
              {{ $t('components.CreateCustomModel.cannotBeUndone') }}
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="gray darken-1" text @click="saveDialog = false">{{ $t('components.CreateCustomModel.abort') }}</v-btn>
              <v-btn color="red darken-1" text @click="triggerEdit">{{ $t('components.CreateCustomModel.confirm') }}</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-dialog v-model="waitingDialog" max-width="400">
          <v-card>
            <v-card-text>
              <v-container fill-height>
              <v-layout row wrap align-center>
                <v-flex style="margin-top: 25px;">
                  <v-progress-circular indeterminate color="primary" style="margin-right: 15px;"></v-progress-circular>{{ $t('components.CreateCustomModel.creatingCustomModel') }}
                </v-flex>
              </v-layout>
            </v-container>
            </v-card-text>
          </v-card>
        </v-dialog>
      </v-toolbar-items>
    </v-toolbar>
    <v-dialog v-model="categoryDialog" max-width="1200px">
      <v-card>
        <v-card-title class="headline">
          {{ categoryDialogTitle }}
          <v-spacer></v-spacer>
          <v-text-field
            v-model="categoryDialogSearch"
            append-icon="mdi-magnify"
            :label="$t('components.CreateCustomModel.search')"
            single-line
            hide-details
          ></v-text-field>
        </v-card-title>
        <v-card-text>
          <v-data-table
            :headers="categoryDialogHeaders"
            :items="categoryDialogItems"
            item-key="id"
            class="elevation-1"
            :search="categoryDialogSearch"
            :custom-filter="categorySearchFilter"
            height="400px"
            sort-by="dateCreatedTimestamp"
            :sort-desc="true"
            :items-per-page="10"
            :footer-props="{
              itemsPerPageText: ''
            }"
          >
            <template v-slot:item.name="{ item }">
              <span v-if="categoryDialogSearch" v-html="highlightText(item.name, categoryDialogSearch)"></span>
              <span v-else>{{ item.name }}</span>
            </template>
            <template v-slot:item.dateCreatedTimestamp="{ item }">
              <span v-if="categoryDialogSearch" v-html="highlightText(item.dateCreated, categoryDialogSearch)"></span>
              <span v-else>{{ item.dateCreated }}</span>
            </template>
            <template v-slot:item.countryName="{ item }">
              <span v-if="categoryDialogSearch" v-html="highlightText(item.countryName, categoryDialogSearch)"></span>
              <span v-else>{{ item.countryName }}</span>
            </template>
            <template v-slot:item.createdBy="{ item }">
              <span v-if="categoryDialogSearch && item.createdByName" v-html="highlightText(item.createdByName, categoryDialogSearch)"></span>
              <span v-else>{{ item.createdByName }}</span>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn small color="#212121" class="white--text" @click="selectCategory(item.id)">
                <span>{{ $t('components.CreateCustomModel.select') }}</span>
              </v-btn>
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="red darken-1"
            text
            @click="categoryDialog = false"
          >
            {{ $t('components.CreateCustomModel.close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-form ref="form" v-model="validForm">
      <v-card-text>
        <v-container>
          <v-row>
            <v-col cols="12" sm="4" md="4">
              <v-text-field v-model="name" :rules="rules.name" :label="$t('components.CreateCustomModel.modelName')" required autofocus></v-text-field>
            </v-col>
            <v-col cols="12" sm="4" md="4">
              <v-select v-model="country" :rules="rules.country" :items="countries" :label="$t('components.CreateCustomModel.country')" item-text="name" item-value="id">
                <template v-slot:append-outer>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon color="primary" dark v-bind="attrs" v-on="on" small>mdi-information-outline</v-icon>
                    </template>
                    <span>{{ $t('components.CreateCustomModel.countryTooltip') }}</span>
                  </v-tooltip>
                </template>
              </v-select>
            </v-col>
          </v-row>
          <v-row v-show="Object.keys(ekrmModels).length > 0 && (authUser.isManager || ((authUser.ekrms && authUser.ekrms.length > 0) || (authUser.groupEkrms && authUser.groupEkrms.length > 0)) || isEkrmManager)">
            <v-col cols="12">
              <v-radio-group v-model="referenceType" mandatory>
                <template v-slot:label>
                  <div>{{ $t('components.CreateCustomModel.referenceType') }}</div>
                  <br>
                </template>
                <v-radio :label="$t('components.CreateCustomModel.ekrmReference')" value="ekrm"></v-radio>
                <v-radio :label="$t('components.CreateCustomModel.standardReference')" value="custom"></v-radio>
              </v-radio-group>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" sm="4" md="4">
              <v-text-field
                v-model="categoryDisplayName"
                :rules="rules.category"
                :label="$t('components.CreateCustomModel.category')"
                readonly
                @click="categoryDialog = true"
                append-icon="mdi-swap-horizontal"
                @click:append="categoryDialog = true"
              >
                <template v-slot:append-outer>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon color="primary" dark v-bind="attrs" v-on="on" small>mdi-information-outline</v-icon>
                    </template>
                    <span>{{ $t('components.CreateCustomModel.categoryTooltip') }}</span>
                  </v-tooltip>
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12" sm="4" md="4">
              <v-select v-if="referenceType === 'custom'" :disabled="!category" v-model="brand" :rules="rules.brand" :items="brands" :label="$t('components.CreateCustomModel.referenceBrands')" item-text="name" item-value="id">
                <template v-slot:append-outer>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon color="primary" dark v-bind="attrs" v-on="on" small>mdi-information-outline</v-icon>
                    </template>
                    <span>{{ $t('components.CreateCustomModel.referenceBrandsTooltip') }}</span>
                  </v-tooltip>
                </template>
              </v-select>
            </v-col>
          </v-row>
          <v-row v-if="referenceVersion === 'base-x3'" v-show="brand">
            <v-col cols="12" sm="4" md="4">
              <v-menu
                v-model="modelStartDateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="modelStartDate"
                    :rules="rules.modelStartDate"
                    :label="$t('components.CreateCustomModel.modelStartDate')"
                    prepend-icon="mdi-calendar"
                    readonly
                    v-on="on"
                  >
                    <template v-slot:append-outer>
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon color="primary" dark v-bind="attrs" v-on="on" small>mdi-information-outline</v-icon>
                        </template>
                        <span>{{ $t('components.CreateCustomModel.modelStartDateTooltip') }}</span>
                      </v-tooltip>
                    </template>
                  </v-text-field>
                </template>
                <v-date-picker v-model="modelStartDate" @input="modelStartDateMenu = false" type="month" :min="minModelStartDate" :max="maxModelStartDate">
                </v-date-picker>
              </v-menu>
            </v-col>
          </v-row>
          <v-row v-show="brand">
            <v-subheader>{{ $t('components.CreateCustomModel.sales') }}</v-subheader>
          </v-row>
          <v-row v-show="brand">
            <v-col cols="12" sm="4" md="4">
              <v-text-field v-model.lazy="avgMonthlySales" ref="avg_monthly_sales" :rules="rules.avgMonthlySales" :label="`${$t('components.CreateCustomModel.averageMonthlySales')} (USD ${this.averageMonthlySalesOriginal})`" prefix="$" v-money="money" required>
                <template v-slot:append-outer>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon color="primary" dark v-bind="attrs" v-on="on" small>mdi-information-outline</v-icon>
                    </template>
                    <span>{{ $t('components.CreateCustomModel.averageMonthlySalesTooltip') }}</span>
                  </v-tooltip>
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12" sm="3" md="3" v-show="unitName && referenceType !== 'ekrm' && unitName !== 'USD'">
              <v-text-field v-model.lazy="usdPerUnit" ref="usd_per_unit" :rules="rules.usdPerUnit" :label="`${$t('components.CreateCustomModel.usdPerUnit')} (${unitName})`" prefix="$" v-money="moneyDecimals" required>
                <template v-slot:append-outer>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon color="primary" dark v-bind="attrs" v-on="on" small>mdi-information-outline</v-icon>
                    </template>
                    <span>{{ $t('components.CreateCustomModel.usdPerUnitTooltip') }}</span>
                  </v-tooltip>
                </template>
              </v-text-field>
            </v-col>
          </v-row>
          <v-row v-show="brand">
            <v-subheader>
              {{ $t('components.CreateCustomModel.spendAndAttribution') }} &nbsp;
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon color="primary" dark v-bind="attrs" v-on="on" small>mdi-information-outline</v-icon>
                </template>
                <span>{{ $t('components.CreateCustomModel.spendAndAttributionTooltip') }}</span>
              </v-tooltip>
            </v-subheader>
          </v-row>
          <v-row v-show="brand">
            <v-col cols="12" sm="4" md="4">
              <v-text-field v-model.lazy="avgMonthlySpend" ref="avg_monthly_invest" :label="`${$t('components.CreateCustomModel.avgMonthlyOwnSpend')} (USD ${this.averageMonthlyInvestOriginal})`" prefix="$" v-money="money"></v-text-field>
            </v-col>
            <v-col cols="12" sm="4" md="4">
              <v-text-field v-show="brand" v-model="marketingSalesPercent" :rules="rules.marketingSalesPercent" :label="marketingSalesPercentTitle" prefix="%" type="number" min="1" max="100"></v-text-field>
              <v-slider
                v-show="brand"
                v-model="marketingSalesPercent"
                min="1"
                max="100"
                step="1"
                style="margin-top: -20px;"
              ></v-slider>
            </v-col>
          </v-row>
          <v-row v-show="brand && averageMonthlyInvestComarketingOriginal !== null && comarketingAttributionOriginal !== null">
            <v-col cols="12" sm="4" md="4">
              <v-text-field v-model.lazy="avgMonthlySpendComarketing" ref="avg_monthly_invest_comarketing" :label="`${$t('components.CreateCustomModel.avgMonthlyComarketingSpend')} (USD ${this.averageMonthlyInvestComarketingOriginal})`" prefix="$" v-money="money"></v-text-field>
            </v-col>
            <v-col cols="12" sm="4" md="4">
              <v-text-field v-show="brand" v-model="comarketingSalesPercent" :label="comarketingSalesPercentTitle" prefix="%" type="number" min="0" max="100"></v-text-field>
              <v-slider
                v-show="brand"
                v-model="comarketingSalesPercent"
                step="1"
                style="margin-top: -20px;"
              ></v-slider>
            </v-col>
          </v-row>
          <v-row v-show="brand && averageMonthlyInvestCompetitionOriginal && competitionAttributionOriginal">
            <v-col cols="12" sm="4" md="4">
              <v-text-field v-model.lazy="avgMonthlySpendCompetition" ref="avg_monthly_invest_competition" :label="avgMonthlySpendCompetitionTitle" prefix="$" v-money="money"></v-text-field>
            </v-col>
            <v-col cols="12" sm="4" md="4">
              <v-text-field v-show="brand" v-model="competitionSalesPercent" :label="competitionSalesPercentTitle" prefix="%" type="number" min="-100" max="0"></v-text-field>
              <v-slider
                v-show="brand"
                v-model="competitionSalesPercent"
                step="1"
                min="-100"
                max="0"
                style="margin-top: -20px;"
              ></v-slider>
            </v-col>
          </v-row>
          <v-row v-if="brand">
            <v-subheader>
              {{ $t('components.CreateCustomModel.averageMediaMix') }} &nbsp;
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon color="primary" dark v-bind="attrs" v-on="on" small>mdi-information-outline</v-icon>
                </template>
                <span>{{ $t('components.CreateCustomModel.averageMediaMixTooltip') }}</span>
              </v-tooltip>
            </v-subheader>
          </v-row>
          <v-row v-if="brand">
            <estimated-channel-attribution @input="updateValue" :channel-name="channel.name" :value="channel.value" :standard-value="channel.standardValue" v-for="channel in channels" :key="`${channel.id}_${brand}_${category}`" />
          </v-row>
          <v-row v-if="channelAttributionError && brand">
            <span class="v-messages error--text pl-1">{{ channelAttributionError }}</span>
          </v-row>
          <v-row v-if="brand && (seasonalitySpotEventsValues || seasonalityPeriodicValues)">
            <v-subheader>{{ $t('components.CreateCustomModel.seasonality') }}</v-subheader>
          </v-row>
          <v-row v-if="brand && seasonalityPeriodicValues">
            <v-col cols="12" sm="12" md="12">
              <external-series-chart v-model="seasonalityPeriodicChanges" :series="{default_values: seasonalityPeriodicValues, min: 0, unit: '%'}" :dates="allDates" :name="$t('components.CreateCustomModel.periodicBoostingEffect')" :start="0" :length="12" :propagateExternalSeriesChanges="false" :info="$t('components.CreateCustomModel.periodicBoostingInfo')" />
            </v-col>
          </v-row>
          <v-row v-if="brand && seasonalitySpotEventsValues">
            <v-col cols="12" sm="12" md="12">
              <external-series-chart v-model="seasonalitySpotEventsChanges" :series="{default_values: seasonalitySpotEventsValues, min: 0, unit: '%'}" :dates="allDates" :name="$t('components.CreateCustomModel.spotEventsBoostingEffect')" :start="0" :length="12" :propagateExternalSeriesChanges="false" :info="$t('components.CreateCustomModel.spotEventsBoostingInfo')" />
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
    </v-form>
  </v-card>
</template>

<script>
import { VMoney } from 'v-money'
import { mapActions, mapGetters } from 'vuex'
import EstimatedChannelAttribution from '@/components/EstimatedChannelAttribution.vue'
import ExternalSeriesChart from '@/components/ExternalSeriesChart.vue'
import numbro from 'numbro'
import moment from 'moment'
import { isComposerEkrm } from '@/utils/ekrmOrigin.js'

export default {
  components: {
    EstimatedChannelAttribution,
    ExternalSeriesChart
  },
  data () {
    return {
      referenceType: 'ekrm',
      modelStartDate: '',
      modelStartDateMenu: false,
      processing: false,
      validForm: true,
      name: '',
      categoryInternal: null,
      brand: null,
      country: null,
      isInitializingFromEdit: false,
      avgMonthlySpend: 0,
      avgMonthlySpendComarketing: 0,
      avgMonthlySpendCompetition: 0,
      avgMonthlySales: 0,
      marketingSalesPercent: 0,
      marketingSalesPercentOriginal: 0,
      comarketingSalesPercent: 0,
      competitionSalesPercent: 0,
      channelAttribution: {},
      usdPerUnit: '1.0',
      firstBrandChange: true,
      saveDialog: false,
      waitingDialog: false,
      categoryDialog: false,
      categoryDialogSearch: '',
      money: {
        thousands: ',',
        precision: 0
      },
      moneyDecimals: {
        decimal: '.',
        thousands: ',',
        precision: 1
      },
      allDates: ['2000-01-01', '2000-02-01', '2000-03-01', '2000-04-01', '2000-05-01', '2000-06-01', '2000-07-01', '2000-08-01', '2000-09-01', '2000-10-01', '2000-11-01', '2000-12-01'],
      seasonalityPeriodicChanges: [],
      seasonalitySpotEventsChanges: []
    }
  },
  methods: {
    ...mapActions('clients', [
      'setCurrentClient',
      'loadNewClient'
    ]),
    ...mapActions('manager', [
      'createCustomModel',
      'editCustomModel'
    ]),
    categorySearchFilter (_value, search, item) {
      // _value parameter is required by Vuetify but not used in our custom filter
      if (!search || search.trim() === '') return true

      const searchLower = search.toLowerCase().trim()

      // Only search in visible columns - explicitly check each field
      const nameMatch = item.name ? item.name.toLowerCase().includes(searchLower) : false
      const countryMatch = item.countryName ? item.countryName.toLowerCase().includes(searchLower) : false
      const dateMatch = item.dateCreated ? item.dateCreated.toLowerCase().includes(searchLower) : false
      const createdByMatch = item.createdByName ? item.createdByName.toLowerCase().includes(searchLower) : false

      // Return true only if at least one visible field matches
      return nameMatch || countryMatch || dateMatch || createdByMatch
    },
    highlightText (text, searchQuery) {
      if (!searchQuery || !text) {
        return text
      }

      // Escape special regex characters in the search query
      const escapedQuery = searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

      // Create case-insensitive regex
      const regex = new RegExp(`(${escapedQuery})`, 'gi')

      // Replace matches with highlighted version
      return text.replace(regex, '<span class="search-highlight">$1</span>')
    },
    selectCategory (categoryId) {
      this.category = categoryId
      this.categoryDialog = false
    },
    openWaitingDialog () {
      return new Promise((resolve) => {
        this.waitingDialog = true
        setTimeout(() => {
          this.waitingDialog = false
          resolve()
        }, 5000)
      })
    },
    recalculateReferenceDates (reference, numberOfMonths) {
      let all = []
      for (let months = 0; months < numberOfMonths; months++) {
        let currentDateMoment = moment(this.modelStartDate).add(months, 'months')
        all.push(currentDateMoment.format('YYYY-MM-') + currentDateMoment.daysInMonth())
      }
      let recalculated = { ...reference }
      console.log(recalculated)
      if ('dates' in recalculated.config.brands[this.brand]) {
        recalculated.config.brands[this.brand].dates.start = `${this.modelStartDate}-01`
        recalculated.config.brands[this.brand].dates.all = all
        recalculated.config.brands[this.brand].dates.end = all[all.length - 1]
      } else {
        recalculated.config.dates.start = `${this.modelStartDate}-01`
        recalculated.config.dates.all = all
        recalculated.config.dates.end = all[all.length - 1]
      }
      return recalculated
    },
    convertSeriesToNumbers (series) {
      return series.map(text => {
        if (text !== null) {
          return Number(text)
        } else {
          return null
        }
      })
    },
    closeDialog () {
      this.$emit('close-dialog')
    },
    validateForm () {
      return this.$refs.form.validate() && !this.channelAttributionError
    },
    triggerCreate () {
      if (!this.processing) {
        this.processing = true
        if (this.validateForm()) {
          let modelObject = {
            user: this.user.uid,
            userName: `${this.user.firstName} ${this.user.lastName}`,
            model: {
              code: this.name,
              name: this.name,
              type: this.referenceType,
              reference_model: this.referenceVersion === 'base-x3' ? this.recalculateReferenceDates(this.referenceModels[this.category], 24) : this.referenceModels[this.category],
              custom_model: {
                average_monthly_invest: Number(this.removeNonDigits(this.avgMonthlySpend)),
                average_monthly_sales: Number(this.removeNonDigits(this.avgMonthlySales)),
                usd_per_unit: Number(this.removeNonDigits(this.usdPerUnit)),
                brand: this.brand,
                marketing_attribution: {
                  percentaje: this.marketingSalesPercent,
                  media: this.channelAttribution
                },
                country: this.country,
                seasonality_periodic_values: this.seasonalityPeriodicChanges ? this.convertSeriesToNumbers(this.seasonalityPeriodicChanges) : null,
                seasonality_spot_events_values: this.seasonalitySpotEventsChanges ? this.convertSeriesToNumbers(this.seasonalitySpotEventsChanges) : null
              }
            }
          }
          if (this.referenceVersion === 'base-x3') {
            modelObject.model.custom_model['model_start_date'] = this.modelStartDate
          }
          if (this.averageMonthlyInvestComarketingOriginal !== null && this.comarketingAttributionOriginal !== null) {
            modelObject.model.custom_model['comarketing_attribution'] = this.comarketingSalesPercent
            modelObject.model.custom_model['average_monthly_invest_comarketing'] = Number(this.removeNonDigits(this.avgMonthlySpendComarketing))
          }
          if (this.averageMonthlyInvestCompetitionOriginal !== null && this.competitionAttributionOriginal !== null) {
            modelObject.model.custom_model['competition_attribution'] = this.competitionSalesPercent
            modelObject.model.custom_model['average_monthly_invest_competition'] = Number(this.removeNonDigits(this.avgMonthlySpendCompetition))
          }
          this.createCustomModel(modelObject).then(id => {
            this.openWaitingDialog().then(() => {
              this.closeDialog()
              if (this.user.isCustomizer || this.isEkrmManager) {
                this.loadNewClient({
                  id: id
                }).then(() => {
                  this.setCurrentClient(id)
                  this.$router.push({ name: 'modelReport' })
                })
              } else {
                this.setCurrentClient(id)
                this.$router.push({ name: 'modelReport' })
              }
              this.processing = false
            })
          }).catch(error => {
            console.log(error)
            this.processing = false
          })
        } else {
          this.processing = false
          console.log('Failed form validation')
        }
      } else {
        console.log('Caught duplicated click')
      }
    },
    triggerEdit () {
      if (this.validateForm()) {
        let modelObject = {
          user: this.user.uid,
          userName: `${this.user.firstName} ${this.user.lastName}`,
          client: this.editing.id,
          date_created: this.editing.date_created,
          created_by: this.editing.created_by,
          created_by_name: this.editing.created_by_name,
          model: {
            code: this.name,
            name: this.name,
            type: this.referenceType,
            reference_model: this.referenceVersion === 'base-x3' ? this.recalculateReferenceDates(this.referenceModels[this.category], 24) : this.referenceModels[this.category],
            custom_model: {
              average_monthly_invest: Number(this.removeNonDigits(this.avgMonthlySpend)),
              average_monthly_sales: Number(this.removeNonDigits(this.avgMonthlySales)),
              usd_per_unit: Number(this.removeNonDigits(this.usdPerUnit)),
              brand: this.brand,
              marketing_attribution: {
                percentaje: this.marketingSalesPercent,
                media: this.channelAttribution
              },
              country: this.country,
              seasonality_periodic_values: this.seasonalityPeriodicChanges ? this.convertSeriesToNumbers(this.seasonalityPeriodicChanges) : null,
              seasonality_spot_events_values: this.seasonalitySpotEventsChanges ? this.convertSeriesToNumbers(this.seasonalitySpotEventsChanges) : null
            }
          }
        }
        if (this.referenceVersion === 'base-x3') {
          modelObject.model.custom_model['model_start_date'] = this.modelStartDate
        }
        if (this.averageMonthlyInvestComarketingOriginal !== null && this.comarketingAttributionOriginal !== null) {
          modelObject.model.custom_model['comarketing_attribution'] = this.comarketingSalesPercent
          modelObject.model.custom_model['average_monthly_invest_comarketing'] = Number(this.removeNonDigits(this.avgMonthlySpendComarketing))
        }
        if (this.averageMonthlyInvestCompetitionOriginal !== null && this.competitionAttributionOriginal !== null) {
          modelObject.model.custom_model['competition_attribution'] = this.competitionSalesPercent
          modelObject.model.custom_model['average_monthly_invest_competition'] = Number(this.removeNonDigits(this.avgMonthlySpendCompetition))
        }
        this.editCustomModel(modelObject).then(id => {
          this.saveDialog = false
          this.closeDialog()
          this.setCurrentClient(id)
          this.$router.push({ name: 'modelReport' })
        }).catch(error => {
          console.log(error)
        })
      }
    },
    removeNonDigits (str) {
      return str.toString().replace(/[^0-9.]/g, '')
    },
    updateValue (e) {
      this.$set(this.channelAttribution, e.key, e.value)
    },
    formatValue: function (value) {
      return numbro(value).format({
        average: true,
        lowPrecision: false,
        mantissa: 1,
        optionalMantissa: true
      })
    },
    getCountryCodeForModel: function (model) {
      // For EKRM models
      if (model.definition && model.definition.reference && model.definition.reference.market) {
        return model.definition.reference.market
      }
      // For Standard Reference models
      if (model.config && model.config.brands) {
        const firstBrand = Object.keys(model.config.brands)[0]
        if (firstBrand && model.config.brands[firstBrand] && model.config.brands[firstBrand].country) {
          return model.config.brands[firstBrand].country
        }
      }
      return null
    }
  },
  computed: {
    ...mapGetters('auth', [
      'authUser',
      'isEkrmManager'
    ]),
    ...mapGetters('settings', [
      'countries'
    ]),
    rules () {
      return {
        name: [
          v => !!v || this.$t('components.CreateCustomModel.required')
        ],
        category: [
          v => !!v || this.$t('components.CreateCustomModel.required')
        ],
        brand: [
          v => !!v || this.$t('components.CreateCustomModel.required')
        ],
        country: [
          v => !!v || this.$t('components.CreateCustomModel.required')
        ],
        avgMonthlySpend: [
          v => (!!v && v !== '0') || this.$t('components.CreateCustomModel.required')
        ],
        avgMonthlySales: [
          v => (!!v && v !== '0') || this.$t('components.CreateCustomModel.required')
        ],
        usdPerUnit: [
          v => (!!v && v !== '0') || this.$t('components.CreateCustomModel.required')
        ],
        modelStartDate: [
          v => !!v || this.$t('components.CreateCustomModel.required')
        ],
        textValue: [
          v => (!!v && v !== '0') || this.$t('components.CreateCustomModel.required')
        ],
        marketingSalesPercent: [
          v => (!!v && Number(v) > 0) || this.$t('components.CreateCustomModel.mustBeGreaterThanZero')
        ]
      }
    },
    referenceModels () {
      if (this.referenceType === 'custom') {
        return this.standardReferenceModels
      } else {
        let parsedModels = { ...this.ekrmModels }
        for (const id of Object.keys(parsedModels)) {
          if (typeof parsedModels[id].config === 'string') {
            parsedModels[id].config = JSON.parse(parsedModels[id].config)
          }
        }
        return parsedModels
      }
    },
    category: {
      get: function () {
        return this.categoryInternal
      },
      set: function (newValue) {
        // Skip resetting values when initializing from editing data
        if (!this.isInitializingFromEdit) {
          this.channelAttribution = {}
          this.brand = null
        }
        this.categoryInternal = newValue
      }
    },
    marketingSalesPercentTitle: function () {
      return `${this.$t('components.CreateCustomModel.salesAttributedToOwnMedia')} (${this.marketingSalesPercentOriginal}%)`
    },
    comarketingSalesPercentTitle: function () {
      return `${this.$t('components.CreateCustomModel.salesAttributedToComarketing')} (${this.comarketingAttributionOriginal}%)`
    },
    competitionSalesPercentTitle: function () {
      const defaultTitle = `${this.$t('components.CreateCustomModel.salesAttributedToCompetitionMedia')} (${this.competitionAttributionOriginal}%)`
      // TODO: This is a hardcoded special case for a specific Reference Model, should be properly coded or removed inthe future
      if (this.category && this.category.startsWith('ref-beers')) {
        return `${this.$t('components.CreateCustomModel.salesAttributedToCompetitionSales')} (${this.competitionAttributionOriginal}%)`
      } else {
        return defaultTitle
      }
    },
    avgMonthlySpendCompetitionTitle: function () {
      const defaultTitle = `${this.$t('components.CreateCustomModel.avgMonthlyCompetitionSpend')} (USD ${this.averageMonthlyInvestCompetitionOriginal})`
      // TODO: This is a hardcoded special case for a specific Reference Model, should be properly coded or removed inthe future
      if ((this.category && this.category.startsWith('ref-retail-stores-2023')) || (this.category && this.category.startsWith('ref-beers'))) {
        return `${this.$t('components.CreateCustomModel.avgMonthlyCompetitionSales')} (USD ${this.averageMonthlyInvestCompetitionOriginal})`
      } else {
        return defaultTitle
      }
    },
    categoryDisplayName: function () {
      if (this.category && this.referenceModels[this.category]) {
        return this.referenceModels[this.category].config.category
      }
      return ''
    },
    categoryDialogTitle: function () {
      return this.referenceType === 'ekrm' ? this.$t('components.CreateCustomModel.selectEkrmCategory') : this.$t('components.CreateCustomModel.selectStandardCategory')
    },
    categoryDialogHeaders: function () {
      const headers = [
        { text: this.$t('components.CreateCustomModel.categoryName'), value: 'name' },
        { text: this.$t('components.CreateCustomModel.country'), value: 'countryName' },
        { text: this.$t('components.CreateCustomModel.dateCreated'), value: 'dateCreatedTimestamp' }
      ]

      // Only show Created By column for EKRM categories
      if (this.referenceType === 'ekrm') {
        headers.push({ text: this.$t('components.CreateCustomModel.createdBy'), value: 'createdBy' })
      }

      headers.push({ text: '', value: 'actions', sortable: false, align: 'end' })
      return headers
    },
    categoryDialogItems: function () {
      return this.categories
    },
    categories: function () {
      return Object.keys(this.referenceModels).map((key) => {
        const model = this.referenceModels[key]
        if (!model || model.disabled || !model.config || !model.config.category) {
          return
        }
        // Not offered as a reference, though still in referenceModels so that cloning or editing a
        // model the composer built keeps finding it
        if (this.referenceType === 'ekrm' && isComposerEkrm(model)) {
          return
        }
        const countryCode = this.getCountryCodeForModel(model)
        const countryObj = this.countries.find(c => c.id === countryCode)
        const countryName = countryObj ? countryObj.name : countryCode || 'N/A'

        // TODO: Customizer user workaround. Should be replaced by new permissions system
        if (this.user.isAdmin || this.user.isScientist || this.user.isManager) {
          return {
            name: model.config.category,
            id: key,
            countryCode: countryCode,
            countryName: countryName,
            dateCreated: model.date_created ? moment(model.date_created.toDate()).format('MMM DD, YYYY h:mm A') : 'N/A',
            dateCreatedTimestamp: model.date_created ? model.date_created.toDate().getTime() : 0,
            createdByName: model.created_by ? model.created_by.slice(model.created_by.indexOf('_') + 1) : 'N/A'
          }
        } else if (this.referenceType === 'custom' && (this.user.references.includes(model.codename) || this.user.groupReferences.includes(model.codename))) {
          return {
            name: model.config.category,
            id: key,
            countryCode: countryCode,
            countryName: countryName,
            dateCreated: model.date_created ? moment(model.date_created.toDate()).format('MMM DD, YYYY h:mm A') : 'N/A',
            dateCreatedTimestamp: model.date_created ? model.date_created.toDate().getTime() : 0
          }
        } else if (this.referenceType === 'ekrm' && (this.user.ekrms.includes(model.codename) || this.user.groupEkrms.includes(model.codename) || this.isEkrmManager)) {
          return {
            name: model.config.category,
            id: key,
            countryCode: countryCode,
            countryName: countryName,
            dateCreated: model.date_created ? moment(model.date_created.toDate()).format('MMM DD, YYYY h:mm A') : 'N/A',
            dateCreatedTimestamp: model.date_created ? model.date_created.toDate().getTime() : 0,
            createdByName: model.created_by ? model.created_by.slice(model.created_by.indexOf('_') + 1) : 'N/A'
          }
        }
      }).filter(element => !!element).sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    },
    brands: function () {
      if (this.category) {
        return Object.keys(this.referenceModels[this.category].config.brands).sort((a, b) => a.localeCompare(b)).map((key, index) => {
          return {
            name: `Reference ${index + 1} (${key}, etc) ~$${this.formatValue(this.referenceModels[this.category].config.brands[key].average_monthly_sales)} monthly sales`,
            id: key
          }
        })
      } else {
        return []
      }
    },
    channels: function () {
      if (this.brand && this.category) {
        return Object.keys(this.referenceModels[this.category].config.brands[this.brand].marketing_attribution.media).map((key) => {
          let value = this.referenceModels[this.category].config.brands[this.brand].marketing_attribution.media[key]
          let standardValue = value
          // If editing, and category-brand are the same as the originals, keep original %
          if (this.editing && this.category === this.editing.reference_model.codename & this.brand === this.editing.custom_model.brand) {
            value = this.editing.custom_model.marketing_attribution.media[key] || 0
          }
          return {
            name: key,
            id: key,
            value: value,
            standardValue: standardValue
          }
        }).sort((a, b) => a.name.localeCompare(b.name))
      } else {
        return []
      }
    },
    channelAttributionError: function () {
      if (this.channelAttribution && Object.keys(this.channelAttribution).length > 0) {
        var total = Object.values(this.channelAttribution).reduce(function (a, b) { return Number(a) + Number(b) })
        if (total !== 100) {
          return this.$t('components.CreateCustomModel.channelAttributionError', { total: total })
        }
      }
      return null
    },
    unitName: function () {
      const propertyName = 'usd_per_unit_name'
      if (this.brand && this.category && 'brands' in this.referenceModels[this.category].config && this.brand in this.referenceModels[this.category].config.brands && propertyName in this.referenceModels[this.category].config.brands[this.brand]) {
        return this.referenceModels[this.category].config.brands[this.brand][propertyName]
      } else {
        return null
      }
    },
    averageMonthlySalesOriginal: function () {
      const propertyName = 'average_monthly_sales'
      if (this.brand && this.category && 'brands' in this.referenceModels[this.category].config && this.brand in this.referenceModels[this.category].config.brands && propertyName in this.referenceModels[this.category].config.brands[this.brand]) {
        return this.formatValue(this.referenceModels[this.category].config.brands[this.brand][propertyName])
      } else {
        return null
      }
    },
    averageMonthlyInvestOriginal: function () {
      const propertyName = 'average_monthly_invest'
      if (this.brand && this.category && 'brands' in this.referenceModels[this.category].config && this.brand in this.referenceModels[this.category].config.brands && propertyName in this.referenceModels[this.category].config.brands[this.brand]) {
        return this.formatValue(this.referenceModels[this.category].config.brands[this.brand][propertyName])
      } else {
        return null
      }
    },
    averageMonthlyInvestComarketingOriginal: function () {
      const propertyName = 'average_monthly_invest_comarketing'
      if (this.brand && this.category && 'brands' in this.referenceModels[this.category].config && this.brand in this.referenceModels[this.category].config.brands && propertyName in this.referenceModels[this.category].config.brands[this.brand]) {
        return this.formatValue(this.referenceModels[this.category].config.brands[this.brand][propertyName])
      } else {
        return null
      }
    },
    comarketingAttributionOriginal: function () {
      const propertyName = 'comarketing_attribution'
      if (this.brand && this.category && 'brands' in this.referenceModels[this.category].config && this.brand in this.referenceModels[this.category].config.brands && propertyName in this.referenceModels[this.category].config.brands[this.brand]) {
        return this.referenceModels[this.category].config.brands[this.brand][propertyName]
      } else {
        return null
      }
    },
    averageMonthlyInvestCompetitionOriginal: function () {
      const propertyName = 'average_monthly_invest_competition'
      if (this.brand && this.category && 'brands' in this.referenceModels[this.category].config && this.brand in this.referenceModels[this.category].config.brands && propertyName in this.referenceModels[this.category].config.brands[this.brand]) {
        return this.formatValue(this.referenceModels[this.category].config.brands[this.brand][propertyName])
      } else {
        return null
      }
    },
    competitionAttributionOriginal: function () {
      const propertyName = 'competition_attribution'
      if (this.brand && this.category && 'brands' in this.referenceModels[this.category].config && this.brand in this.referenceModels[this.category].config.brands && propertyName in this.referenceModels[this.category].config.brands[this.brand]) {
        return this.referenceModels[this.category].config.brands[this.brand][propertyName]
      } else {
        return null
      }
    },
    seasonalityPeriodicValues: function () {
      const propertyName = 'seasonality_periodic_values'
      if (this.brand && this.category && 'brands' in this.referenceModels[this.category].config && this.brand in this.referenceModels[this.category].config.brands && propertyName in this.referenceModels[this.category].config.brands[this.brand]) {
        return this.referenceModels[this.category].config.brands[this.brand][propertyName]
      } else {
        return null
      }
    },
    seasonalitySpotEventsValues: function () {
      const propertyName = 'seasonality_spot_events_values'
      if (this.brand && this.category && 'brands' in this.referenceModels[this.category].config && this.brand in this.referenceModels[this.category].config.brands && propertyName in this.referenceModels[this.category].config.brands[this.brand]) {
        return this.referenceModels[this.category].config.brands[this.brand][propertyName]
      } else {
        return null
      }
    },
    minModelStartDate: function () {
      if (this.referenceType === 'ekrm') {
        return '2015-01-01'
      } else if (this.brand && this.category && 'brands' in this.referenceModels[this.category].config && this.brand in this.referenceModels[this.category].config.brands && 'dates' in this.referenceModels[this.category].config.brands[this.brand]) {
        return this.referenceModels[this.category].config.brands[this.brand].dates.start
      } else if (this.category && 'dates' in this.referenceModels[this.category].config && 'start' in this.referenceModels[this.category].config.dates) {
        return this.referenceModels[this.category].config.dates.start
      } else {
        return null
      }
    },
    maxModelStartDate: function () {
      let extraYears = 6
      if (this.referenceType === 'ekrm') {
        extraYears = 15
      }
      return moment(this.minModelStartDate).add(extraYears, 'years').format('YYYY-MM-DD')
    },
    referenceVersion: function () {
      if (this.category && this.referenceModels[this.category].config.metadata && this.referenceModels[this.category].config.metadata.image_family) {
        return this.referenceModels[this.category].config.metadata.image_family
      } else {
        return null
      }
    }
  },
  watch: {
    referenceModels: {
      handler: function (models) {
        if (!models) return
        if (!this._warnedCorruptModels) this._warnedCorruptModels = new Set()
        Object.keys(models).forEach((key) => {
          const model = models[key]
          if (!model) return
          if (!model.config || !model.config.category) {
            if (this._warnedCorruptModels.has(key)) return
            this._warnedCorruptModels.add(key)
            console.warn(`[CreateCustomModel] Skipping corrupt reference model "${key}" (codename: ${model.codename || 'N/A'}): missing config.category`)
          }
        })
      },
      immediate: true
    },
    referenceType: function (newVal) {
      if (newVal && !this.isInitializingFromEdit) {
        this.categoryInternal = null
        this.brand = null
      }
    },
    categoryInternal: function () {
      // Skip during initialization to avoid overriding the brand from editing data
      if (this.isInitializingFromEdit) {
        return
      }
      if (this.brands.length === 1) {
        this.brand = this.brands[0].id
      }
    },
    'brand': function () {
      // Skip during initialization from editing data
      if (this.isInitializingFromEdit) {
        return
      }
      if (this.brand && this.category) {
        this.marketingSalesPercentOriginal = this.referenceModels[this.category].config.brands[this.brand].marketing_attribution.percentaje
        // Hacky way of not overriding margetinkSalesPercents and usdPerUnit, on startup, when editing a model
        if (!this.editing || (this.editing && !this.firstBrandChange)) {
          this.marketingSalesPercent = this.referenceModels[this.category].config.brands[this.brand].marketing_attribution.percentaje
          if (this.referenceModels[this.category].config.brands[this.brand].comarketing_attribution) {
            this.comarketingSalesPercent = this.referenceModels[this.category].config.brands[this.brand].comarketing_attribution
          }
          if (this.referenceModels[this.category].config.brands[this.brand].competition_attribution) {
            this.competitionSalesPercent = this.referenceModels[this.category].config.brands[this.brand].competition_attribution
          }
          if (this.referenceModels[this.category].config.brands[this.brand].average_monthly_sales) {
            // Hacky way of doing this, but apparentlythe only way using v-money
            // https://github.com/vuejs-tips/v-money/issues/44
            this.$nextTick(() => {
              this.$refs.avg_monthly_sales.$el.getElementsByTagName('input')[0].value = this.referenceModels[this.category].config.brands[this.brand].average_monthly_sales
              this.avgMonthlySales = this.referenceModels[this.category].config.brands[this.brand].average_monthly_sales
            })
          }
          if (this.referenceModels[this.category].config.brands[this.brand].average_monthly_invest) {
            // Hacky way of doing this, but apparentlythe only way using v-money
            // https://github.com/vuejs-tips/v-money/issues/44
            this.$nextTick(() => {
              this.$refs.avg_monthly_invest.$el.getElementsByTagName('input')[0].value = this.referenceModels[this.category].config.brands[this.brand].average_monthly_invest
              this.avgMonthlySpend = this.referenceModels[this.category].config.brands[this.brand].average_monthly_invest
            })
          }
          if (this.referenceModels[this.category].config.brands[this.brand].average_monthly_invest_competition) {
            // Hacky way of doing this, but apparentlythe only way using v-money
            // https://github.com/vuejs-tips/v-money/issues/44
            this.$nextTick(() => {
              this.$refs.avg_monthly_invest_competition.$el.getElementsByTagName('input')[0].value = this.referenceModels[this.category].config.brands[this.brand].average_monthly_invest_competition
              this.avgMonthlySpendCompetition = this.referenceModels[this.category].config.brands[this.brand].average_monthly_invest_competition
            })
          }
          if (this.referenceModels[this.category].config.brands[this.brand].average_monthly_invest_comarketing) {
            // Hacky way of doing this, but apparentlythe only way using v-money
            // https://github.com/vuejs-tips/v-money/issues/44
            this.$nextTick(() => {
              this.$refs.avg_monthly_invest_comarketing.$el.getElementsByTagName('input')[0].value = this.referenceModels[this.category].config.brands[this.brand].average_monthly_invest_comarketing
              this.avgMonthlySpendComarketing = this.referenceModels[this.category].config.brands[this.brand].average_monthly_invest_comarketing
            })
          }
          if ('usd_per_unit' in this.referenceModels[this.category].config.brands[this.brand]) {
            // Hacky way of doing this, but apparentlythe only way using v-money
            // https://github.com/vuejs-tips/v-money/issues/44
            this.$nextTick(() => {
              this.$refs.usd_per_unit.$el.getElementsByTagName('input')[0].value = this.referenceModels[this.category].config.brands[this.brand].usd_per_unit.toFixed(1)
              this.usdPerUnit = this.referenceModels[this.category].config.brands[this.brand].usd_per_unit.toFixed(1)
            })
          }
        } else if (this.editing && this.firstBrandChange) {
          // On next watch triggered for brand, enter the if above
          this.firstBrandChange = false
        }
      }
    },
    editing: {
      immediate: true,
      handler: function () {
        if (this.editing) {
          var model = this.editing.custom_model
          // Set flag to prevent category setter from clearing values
          this.isInitializingFromEdit = true
          this.referenceType = this.editing.type
          this.name = this.cloning ? `${this.editing.name} (copy)` : this.editing.name
          this.category = this.editing.reference_model.codename
          this.brand = model.brand
          this.country = model.country
          this.modelStartDate = model.model_start_date ? model.model_start_date : ''
          this.avgMonthlySpend = model.average_monthly_invest | 0
          this.avgMonthlySpendComarketing = model.average_monthly_invest_comarketing | 0
          this.avgMonthlySpendCompetition = model.average_monthly_invest_competition | 0
          this.avgMonthlySales = model.average_monthly_sales | 0
          this.marketingSalesPercent = model.marketing_attribution.percentaje | 0
          this.comarketingSalesPercent = model.comarketing_attribution | 0
          this.competitionSalesPercent = Math.min(model.competition_attribution, 0) | 0 // Filter positive values when cloning old models
          this.usdPerUnit = model.usd_per_unit.toFixed(1)
          // Populate channelAttribution from the editing data
          if (model.marketing_attribution && model.marketing_attribution.media) {
            this.channelAttribution = { ...model.marketing_attribution.media }
          }
          this.seasonalityPeriodicChanges = model.seasonality_periodic_values.map(element => {
            if (element !== null) {
              return element.toString()
            } else {
              return element
            }
          })
          this.seasonalitySpotEventsChanges = model.seasonality_spot_events_values.map(element => {
            if (element !== null) {
              return element.toString()
            } else {
              return element
            }
          })
          // Reset the flag after initialization is complete
          // Use $nextTick to ensure all watchers have processed
          this.$nextTick(() => {
            this.isInitializingFromEdit = false
          })
        }
      }
    }
  },
  directives: {
    money: VMoney
  },
  props: {
    user: {
      type: Object,
      required: true
    },
    standardReferenceModels: {
      type: Object,
      required: true
    },
    ekrmModels: {
      type: Object,
      required: true
    },
    editing: {
      type: Object,
      required: false
    },
    cloning: {
      type: Boolean,
      required: false,
      default: function () {
        return false
      }
    }
  }
}
</script>

<style>
  .search-highlight {
    background-color: yellow;
    font-weight: inherit;
  }
</style>
